import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { celebrityIPPacksAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatRelativeTime } from '../lib/utils';
import {
  Plus,
  Palette,
  Search,
  MoreVertical,
  Trash2,
  Eye,
  CheckCircle,
  Clock,
  FileText,
  Package,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-zinc-100 text-zinc-700', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  in_production: { label: 'In Production', color: 'bg-blue-100 text-blue-700', icon: Package },
};

const CelebrityIPList = () => {
  const navigate = useNavigate();
  const { activeBrand } = useAuth();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newPack, setNewPack] = useState({
    title: '',
    price_sold: '',
  });

  useEffect(() => {
    fetchPacks();
  }, [activeBrand]);

  const fetchPacks = async () => {
    try {
      setLoading(true);
      const response = await celebrityIPPacksAPI.getAll(activeBrand);
      setPacks(response.data);
    } catch (error) {
      toast.error('Failed to load Celebrity IP packs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePack = async () => {
    if (!newPack.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      const response = await celebrityIPPacksAPI.create(newPack);
      toast.success('Celebrity IP pack created!');
      setShowAddDialog(false);
      setNewPack({ title: '', price_sold: '' });
      navigate(`/celebrity-ip/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create pack');
    }
  };

  const handleDeletePack = async (packId) => {
    try {
      await celebrityIPPacksAPI.delete(packId);
      toast.success('Pack deleted');
      setDeleteConfirm(null);
      fetchPacks();
    } catch (error) {
      toast.error('Failed to delete pack');
    }
  };

  const filteredPacks = packs.filter((pack) => {
    const matchesSearch = pack.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || pack.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatPrice = (price) => {
    if (!price) return '-';
    return price.startsWith('$') ? price : `$${price}`;
  };

  const getMediaCount = (pack) => {
    let count = 0;
    ['customer_reference', 'cad_quote', 'final_product', 'reviews'].forEach((section) => {
      count += pack[section]?.media?.length || 0;
    });
    return count;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="celebrity-ip-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-zinc-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="celebrity-ip-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Palette className="w-8 h-8 text-zinc-700" />
          <div>
            <h1 className="text-3xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Celebrity IP
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Manage celebrity collaboration reels</p>
          </div>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm px-6 py-2"
          data-testid="add-celebrity-ip-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          NEW PRODUCT
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search celebrity IP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm"
            data-testid="celebrity-ip-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-zinc-500">
          {filteredPacks.length} pack{filteredPacks.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* List View */}
      {filteredPacks.length === 0 ? (
        <div className="text-center py-16 bg-zinc-50/50 rounded-sm">
          <Palette className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-zinc-700 mb-2">No Celebrity IP packs yet</h3>
          <p className="text-sm text-zinc-500 mb-6">Create your first celebrity IP pack</p>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Product
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-zinc-50 border-b border-zinc-200 text-xs font-semibold text-zinc-500 uppercase tracking-widest">
            <div className="col-span-5">Title</div>
            <div className="col-span-2">Price Sold</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-1"></div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-zinc-100">
            {filteredPacks.map((pack) => {
              const statusConfig = STATUS_CONFIG[pack.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusConfig.icon;
              const mediaCount = getMediaCount(pack);

              return (
                <div
                  key={pack.id}
                  className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-zinc-50 cursor-pointer transition-colors items-center"
                  onClick={() => navigate(`/celebrity-ip/${pack.id}`)}
                  data-testid={`celebrity-ip-row-${pack.id}`}
                >
                  <div className="col-span-5">
                    <p className="font-medium text-zinc-900">{pack.title}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {mediaCount} media file{mediaCount !== 1 ? 's' : ''}
                      {pack.script ? ' • Has script' : ''}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-zinc-700">{formatPrice(pack.price_sold)}</span>
                  </div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm text-zinc-500">{formatRelativeTime(pack.created_at)}</span>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/celebrity-ip/${pack.id}`); }}>
                          <Eye className="w-4 h-4 mr-2" /> View Details
                        </DropdownMenuItem>
                        {pack.production_pack_id && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/production-packs/${pack.production_pack_id}`); }}>
                            <Package className="w-4 h-4 mr-2" /> View Production Pack
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(pack); }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Pack Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              New Celebrity IP Product
            </DialogTitle>
            <DialogDescription>
              Create a new celebrity IP pack
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newPack.title}
                onChange={(e) => setNewPack({ ...newPack, title: e.target.value })}
                placeholder="e.g., Custom Diamond Ring - Client A"
                className="w-full px-3 py-2 border border-zinc-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-zinc-200"
                data-testid="new-celebrity-ip-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Price Sold
              </label>
              <input
                type="text"
                value={newPack.price_sold}
                onChange={(e) => setNewPack({ ...newPack, price_sold: e.target.value })}
                placeholder="e.g., $150,000"
                className="w-full px-3 py-2 border border-zinc-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-zinc-200"
                data-testid="new-celebrity-ip-price-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreatePack}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="create-celebrity-ip-btn"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Delete Celebrity IP Pack</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.title}"? All associated media will be deleted. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleDeletePack(deleteConfirm.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CelebrityIPList;
